/**
 * Production API Implementation of IMemberRepository
 */
import api from '../../../lib/api';
import { Member, ResidentType, VerificationStatus } from '../types/member';
import { dataUtils } from '../../../lib/dataUtils';
import { IMemberRepository } from './IMemberRepository';

export class ApiMemberRepository implements IMemberRepository {
  async getAll(societyId?: number, signal?: AbortSignal): Promise<Member[]> {
    const url = societyId ? `/members?societyId=${societyId}` : '/members?size=200';
    const response = await api.get(url, { signal });
    return dataUtils.normalizeLookup<Member>(response.data, 'memberId');
  }

  async getResidentTypes(signal?: AbortSignal): Promise<ResidentType[]> {
    const response = await api.get('/members/residentTypes', { signal });
    return dataUtils.normalizeLookup<ResidentType>(response.data, 'residentTypeId', 'residentTypeName');
  }

  async getVerificationStatuses(signal?: AbortSignal): Promise<VerificationStatus[]> {
    const response = await api.get('/members/verificationStatuses', { signal });
    return dataUtils.normalizeLookup<VerificationStatus>(response.data, 'verificationStatusId', 'verificationStatusName');
  }

  async getRoleTypes(signal?: AbortSignal): Promise<any[]> {
    const response = await api.get('/members/roleTypes', { signal });
    return dataUtils.normalizeLookup<any>(response.data, 'roleTypeId', 'roleTypeName');
  }

  async previewCode(firstName: string, middleName?: string, lastName?: string): Promise<string> {
    try {
      const params = new URLSearchParams({
        firstName: firstName || '',
        middleName: middleName || '',
        lastName: lastName || '',
      });
      const response = await api.get(`/members/preview-code?${params.toString()}`);
      const d = response.data;
      if (typeof d?.data === 'string') return d.data;
      if (typeof d?.message === 'string' && d.message.includes('-')) return d.message;
      if (typeof d === 'string') return d;
      return '';
    } catch {
      return '';
    }
  }

  async create(member: Member, signal?: AbortSignal): Promise<Member> {
    const response = await api.post('/members', member, { signal });
    return dataUtils.normalizeItem<Member>(response.data, 'memberId');
  }

  async getById(id: number, signal?: AbortSignal): Promise<Member> {
    const response = await api.get(`/members/${id}`, { signal });
    return dataUtils.normalizeItem<Member>(response.data, 'memberId');
  }

  async update(id: number, member: Member, signal?: AbortSignal): Promise<Member> {
    const response = await api.put(`/members/${id}`, member, { signal });
    return dataUtils.normalizeItem<Member>(response.data, 'memberId');
  }

  async delete(id: number, signal?: AbortSignal): Promise<void> {
    await api.delete(`/members/${id}`, { signal });
  }
}
